import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { store } from '@/app/store';
import { RoleContext } from '@/app/api/common';

import { deletedPrivileges } from '@/features/deleted/deletedApi';

import Button from '@/components/Common/Button';
import DeletedPanel from '@/components/Common/DeletedPanel';
import FlexRow from '@/components/Common/FlexRow';

import { InfoBanner, su } from '@/pages/Settings/settingsUi';

const Deleted = () => {
  const { t } = useTranslation();
  const role = React.useContext(RoleContext);

  // One counter drives every panel, so the header button refreshes the page
  // as a whole rather than each card owning its own link.
  const [refreshSignal, setRefreshSignal] = React.useState(0);

  // Fail CLOSED: every capability defaults to false, and a kind the user
  // cannot view is never rendered — so its list request never fires either.
  const has = React.useCallback(
    (p: string) => !!role?.admin || !!role?.privilages?.includes(p),
    [role]
  );

  const canProjects = has(deletedPrivileges.project);
  const canChannels = has(deletedPrivileges.channel);
  const canLists    = has(deletedPrivileges.channel_list);
  const canUsers    = has(deletedPrivileges.user);

  const anything = canProjects || canChannels || canLists || canUsers;

  return (
  <div className={su.page}>
    {/* Wider than the standard su.pageInner (1040px): these are six-column
        tables with an action column, not a stack of narrow form fields. */}
    <div className="w-full max-w-[1320px] space-y-[20px]">
      <FlexRow align="start" className="pb-[4px] gap-[16px]">
        <FlexRow direction="column" align="start" flex={1} className="gap-[2px]">
          <span className={su.pageTitle}>{ t('deleted:caption') }</span>
          <span className={su.subtitle}>{ t('deleted:page_subtitle') }</span>
        </FlexRow>
        { anything &&
          <FlexRow flex={0} className="shrink-0 pt-[6px]">
            <Button caption={t('deleted:refresh')}
                    icon="refresh"
                    layout="white"
                    onClick={() => setRefreshSignal(refreshSignal + 1)} />
          </FlexRow>
        }
      </FlexRow>

      { !anything &&
        <InfoBanner text={t('deleted:no_access')} />
      }

      {/* `eager` on this page only: the count badges are the point of it, and
          they are meaningless until each list has actually loaded. Panels
          embedded in other pages stay lazy. */}
      <DeletedPanel kind="project"
                    icon="grid_view"
                    title={t('deleted:kind:project')}
                    subtitle={t('deleted:kind:project_hint')}
                    canView={canProjects}
                    eager={true}
                    refreshSignal={refreshSignal}
                    onChanged={() => store.getProjects()} />

      <DeletedPanel kind="channel"
                    icon="smart_display"
                    title={t('deleted:kind:channel')}
                    canView={canChannels}
                    eager={true}
                    refreshSignal={refreshSignal}
                    onChanged={() => store.getChannels()} />

      <DeletedPanel kind="channel_list"
                    icon="list_alt"
                    title={t('deleted:kind:channel_list')}
                    canView={canLists}
                    eager={true}
                    refreshSignal={refreshSignal}
                    onChanged={() => store.getChannelLists()} />

      <DeletedPanel kind="user"
                    icon="group"
                    title={t('deleted:kind:user')}
                    subtitle={t('deleted:kind:user_hint')}
                    canView={canUsers}
                    eager={true}
                    refreshSignal={refreshSignal}
                    onChanged={() => store.getLogins()} />

      { anything &&
        <InfoBanner>
          <span>
            { t('deleted:retention_hint') }
            { ' ' }
            <Link to="/settings/system"
                  className="underline text-[--Primetime-main]">
              { t('deleted:retention_link') }
            </Link>
            { '.' }
          </span>
        </InfoBanner>
      }
    </div>
  </div>
  );
};

export default Deleted;
